/**
 * InfinitSales - Widgets do site: modal de criar widget.
 *
 * So o essencial: nome, endereco do site (opcional, vira o dominio
 * autorizado; o backend normaliza) e, havendo widgets, de qual copiar a
 * aparencia. Modo, posicao, paginas ocultas e o resto ficam no Editar.
 * Duplicar abre este mesmo modal com a aparencia do widget de origem ja
 * escolhida e o nome "(copia)".
 */
import React, { useId, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import widgetService from '../../services/widget/widgetService';
import WidgetModalShell, { ErrorBox, errorMessage, formStyles } from './WidgetModalShell';

// O que "Igual a <nome>" copia: visual e atendimento. Ficam de fora hide_on
// (caminhos do site de origem) e iframe_url.
const APPEARANCE_KEYS = [
  'primary_color', 'secondary_color', 'logo_url', 'chat_logo_url', 'title', 'welcome_message',
  'tooltip', 'bubble_style', 'layout', 'position', 'offset_x', 'offset_y', 'z_index', 'form',
  'away_message', 'turn_failure_message', 'show_member_names',
];

const copyAppearance = (settings) => {
  const out = {};
  APPEARANCE_KEYS.forEach((k) => {
    const v = settings?.[k];
    if (v === undefined) return;
    out[k] = v && typeof v === 'object' ? JSON.parse(JSON.stringify(v)) : v;
  });
  return out;
};

const WidgetCreateModal = ({ accounts = [], defaultSourceId = '', defaultName = '', onClose, onCreated }) => {
  const { colors } = useTheme();
  const s = formStyles(colors);
  const uid = useId();
  const [name, setName] = useState(defaultName);
  const [site, setSite] = useState('');
  const [sourceId, setSourceId] = useState(defaultSourceId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!name.trim()) { setError('Dê um nome ao widget.'); return; }
    setSaving(true);
    setError(null);
    try {
      const source = accounts.find((a) => a.id === sourceId);
      const res = await widgetService.createAccount({
        name: name.trim(),
        mode: 'embed',
        allowed_domains: site.trim() ? [site.trim()] : [],
        widget_settings: source ? copyAppearance(source.widget_settings) : {},
        agent_id: null,
      });
      onCreated?.(res?.data || null);
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível criar o widget.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WidgetModalShell
      title="Novo widget"
      onClose={onClose}
      onSubmit={submit}
      maxWidth={440}
      footer={(
        <>
          {error && <ErrorBox>{error}</ErrorBox>}
          <button type="submit" className="wdg-btn" disabled={saving} style={s.primaryBtn}>
            {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            Criar widget
          </button>
        </>
      )}
    >
      <div style={s.field}>
        <label htmlFor={`${uid}-name`} style={s.label}>Nome</label>
        <input
          id={`${uid}-name`}
          className="wdg-field"
          style={s.input}
          value={name}
          maxLength={80}
          autoFocus
          placeholder="Ex.: Site da loja"
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div style={accounts.length ? s.field : undefined}>
        <label htmlFor={`${uid}-site`} style={s.label}>
          Endereço do site <span style={s.optional}>(opcional)</span>
        </label>
        <input
          id={`${uid}-site`}
          className="wdg-field"
          style={s.input}
          value={site}
          inputMode="url"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder="www.minhaloja.com.br"
          onChange={(e) => setSite(e.target.value)}
        />
      </div>

      {accounts.length > 0 && (
        <div>
          <label htmlFor={`${uid}-look`} style={s.label}>Aparência</label>
          <select
            id={`${uid}-look`}
            className="wdg-field"
            style={s.input}
            value={sourceId}
            onChange={(e) => setSourceId(e.target.value)}
          >
            <option value="">Padrão</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>Igual a {a.name || 'Widget'}</option>
            ))}
          </select>
        </div>
      )}
    </WidgetModalShell>
  );
};

export default WidgetCreateModal;
